import {
  CaretDown,
  CaretUp,
  CheckCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { AccentButton } from '../../../../../../shared/Buttons';
import {
  CustomerWithDriverOnboarding,
  Entity,
  isStepCompleted,
  OnboardingStepId,
} from '../../../../../../types/apiTypes';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import {
  OnboardingStepUI,
  ONBOARDING_STEPS,
} from '../../../../../../types/apiTypes';
import { Customer, OnboardingStep } from '../../../../../../types/dbTypes';
import CertificateOfInsuranceModal from './CertificateOfInsuranceModal';
import DirectDepositInstructionsModal from './DirectDepositInstructionsModal';
import FinalConsentModal from '../modals/FinalConsentModal';
import { Application } from '../../../../../../types/dbTypes';

interface GetStartedWidgetProps {
  application?: Application;
  userId?: string;
}

function GetStartedWidget({ application, userId }: GetStartedWidgetProps) {
  const { userRecord } = useUser();
  const navigate = useNavigate();
  const { screenView } = useScreenView();
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [isDirectDepositModalOpen, setIsDirectDepositModalOpen] =
    useState(false);
  const [isFinalConsentModalOpen, setIsFinalConsentModalOpen] = useState(false);

  // Customers who have been active for a while but still have onboarding steps
  // left shouldn't have the checklist eating the top of their home screen.
  // For them we make it collapsible and auto-collapse it (they can reopen it).
  const activatedAt = (userRecord as Customer)?.activated_at;
  const daysSinceActivation = activatedAt
    ? (Date.now() - new Date(activatedAt).getTime()) / (1000 * 60 * 60 * 24)
    : 0;
  const isCollapsible = daysSinceActivation > 14;
  const [stepsCollapsed, setStepsCollapsed] = useState(isCollapsible);

  const handleStepClick = (step: OnboardingStepUI) => {
    // Special handling for Final Consent step
    if (step.id === OnboardingStepId.FINAL_CONSENT) {
      setIsFinalConsentModalOpen(true);
    } else if (step.id === OnboardingStepId.PENSKE_CERTIFICATE_OF_INSURANCE) {
      // Special handling for Certificate of Insurance steps
      setIsCertificateModalOpen(true);
    } else if (step.id === OnboardingStepId.DIRECT_DEPOSIT) {
      // Special handling for Direct Deposit step
      setIsDirectDepositModalOpen(true);
    } else if (step.route.startsWith('http')) {
      // Check if the route is an external URL (starts with http/https)
      window.open(step.route, '_blank');
    } else {
      navigate(step.route);
    }
  };

  // Helper function to count completed steps
  const getCompletedStepsCount = (
    steps: OnboardingStep | undefined
  ): number => {
    if (!steps) return 0;
    // Count both Penske and Ryder as one combined step
    const certificatesComplete =
      steps.penske_certificate_of_insurance_added &&
      steps.ryder_certificate_of_insurance_added;

    const finalConsentComplete = (userRecord as CustomerWithDriverOnboarding)
      ?.final_consent_completed;

    return [
      finalConsentComplete,
      steps.direct_deposit_verified,
      steps.physical_debit_card_activated,
      steps.asset_info_added,
      certificatesComplete,
    ].filter(Boolean).length;
  };

  // Helper function to check if the combined certificate step is completed
  const isCertificateStepCompleted = (
    steps: OnboardingStep | undefined
  ): boolean => {
    if (!steps) return false;
    return Boolean(
      steps.penske_certificate_of_insurance_added &&
      steps.ryder_certificate_of_insurance_added
    );
  };

  const checkIfOnboardingNotYetComplete = () => {
    const user = userRecord as CustomerWithDriverOnboarding;
    // Adjust for combined certificate step (5 steps instead of 6)
    const totalSteps = ONBOARDING_STEPS.filter(
      (step) => step.id !== OnboardingStepId.RYDER_CERTIFICATE_OF_INSURANCE
    ).length;
    return (
      !user?.final_consent_completed ||
      !user?.onboardingSteps ||
      getCompletedStepsCount(user?.onboardingSteps) < totalSteps
    );
  };

  // Create filtered and modified steps array for display
  const getDisplaySteps = (): OnboardingStepUI[] => {
    return ONBOARDING_STEPS.filter(
      (step) => step.id !== OnboardingStepId.RYDER_CERTIFICATE_OF_INSURANCE
    ).map((step) => {
      if (step.id === OnboardingStepId.PENSKE_CERTIFICATE_OF_INSURANCE) {
        return {
          ...step,
          title: 'Truck Rental Insurance Certificates',
          description: 'Complete Penske & Ryder truck rental onboarding.',
        };
      }
      return step;
    });
  };

  const renderScreenView = (screenView: string) => {
    const displaySteps = getDisplaySteps();

    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          userRecord &&
          userRecord?.userType === Entity.Customer &&
          checkIfOnboardingNotYetComplete() && (
            <div className="flex w-full flex-col gap-y-[24px] bg-ctaactivegreen p-[16px]">
              {isCollapsible ? (
                <button
                  type="button"
                  onClick={() => setStepsCollapsed((collapsed) => !collapsed)}
                  aria-expanded={!stepsCollapsed}
                  className="flex w-full items-center justify-between text-left"
                >
                  <h2 className="text-[20px] font-semibold text-primary">
                    Complete Account Setup (
                    {getCompletedStepsCount(userRecord?.onboardingSteps)}/
                    {displaySteps.length})
                  </h2>
                  {stepsCollapsed ? (
                    <CaretDown
                      weight="bold"
                      className="text-2xl text-primary"
                    />
                  ) : (
                    <CaretUp weight="bold" className="text-2xl text-primary" />
                  )}
                </button>
              ) : (
                <h2 className="text-[20px] font-semibold text-primary">
                  Complete Account Setup (
                  {getCompletedStepsCount(userRecord?.onboardingSteps)}/
                  {displaySteps.length})
                </h2>
              )}
              {!(isCollapsible && stepsCollapsed) && (
                <div className="grid w-[100%] grid-cols-1 items-center justify-center gap-3 align-middle lg:grid-cols-2">
                  {displaySteps.map((step: OnboardingStepUI) => {
                    // Check completion status - special handling for certificate and final consent steps
                    const isCompleted =
                      step.id === OnboardingStepId.FINAL_CONSENT
                        ? Boolean(
                            (userRecord as CustomerWithDriverOnboarding)
                              ?.final_consent_completed
                          )
                        : step.id ===
                            OnboardingStepId.PENSKE_CERTIFICATE_OF_INSURANCE
                          ? isCertificateStepCompleted(
                              userRecord?.onboardingSteps
                            )
                          : isStepCompleted(
                              userRecord?.onboardingSteps,
                              step.id
                            );

                    return (
                      <div
                        key={step.id}
                        className={`radius-[4px] flex h-[150px] w-full flex-row items-center justify-between bg-white align-middle sm:p-2 md:h-[90px] md:max-h-[90px] md:p-4 ${isCompleted && 'opacity-70'}`}
                      >
                        <div className="flex w-full flex-col items-center justify-between space-y-2 align-middle md:flex-row">
                          <div className="flex w-full flex-row items-center justify-between align-middle md:w-[75%]">
                            {isCompleted ? (
                              <CheckCircle
                                weight="fill"
                                className={`w-[15%] text-4xl text-primary`}
                              />
                            ) : (
                              <WarningCircle
                                weight="fill"
                                className={`w-[15%] text-4xl text-ctaactivegreen`}
                              />
                            )}
                            <div className="flex w-[85%] flex-col items-start justify-start">
                              <h2 className="text-[16px] text-primary">
                                {step.title}
                              </h2>
                              <p className="text-[14px] text-graysecondary">
                                {step.description}
                              </p>
                            </div>
                          </div>
                          {!isCompleted ? (
                            <AccentButton
                              addClass="rounded-full sm:py-[2px] md:py-[6px] sm:w-full md:w-auto"
                              onClick={() => handleStepClick(step)}
                            >
                              Start
                            </AccentButton>
                          ) : (
                            <div className="rounded-full bg-primary px-2 py-[1px] text-white">
                              Completed
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )
        );
    }
  };

  return (
    <>
      {renderScreenView(screenView)}
      <CertificateOfInsuranceModal
        isOpen={isCertificateModalOpen}
        onClose={() => setIsCertificateModalOpen(false)}
        onComplete={() => {
          // Reload the component to show updated completion status
          window.location.reload();
        }}
      />
      <DirectDepositInstructionsModal
        isOpen={isDirectDepositModalOpen}
        onClose={() => setIsDirectDepositModalOpen(false)}
        onComplete={() => {
          // Reload the component to show updated completion status
          window.location.reload();
        }}
      />
      {application && userId && (
        <FinalConsentModal
          isOpen={isFinalConsentModalOpen}
          onClose={() => {
            setIsFinalConsentModalOpen(false);
            window.location.reload();
          }}
          application_id={application.application_id}
          application={application}
          userId={userId}
        />
      )}
    </>
  );
}

export default GetStartedWidget;
